import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import { useSelector } from "react-redux";
import { lightTheme, darkTheme } from "./theme";

const ThemeContext = createContext({
	isDarkMode: false,
	toggleTheme: () => {},
	theme: lightTheme
});

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (!context) {
		// Fallback silencieux : retourne le contexte par défaut
		// ou log optionnel si besoin
		console.error("useTheme must be used within a ThemeProvider");
		return {
			isDarkMode: false,
			toggleTheme: () => {},
			theme: lightTheme
		};
	}
	return context;
};

export const ThemeProvider = ({ children }) => {
	// Get dark mode preference from Redux state
	const savedTheme = useSelector(state => state.userStatus?.auth?.user?.cockpitSettings?.darkMode);

	// Initialize from Redux state, default to false (light mode)
	const [isDarkMode, setIsDarkMode] = useState(() => {
		return savedTheme === true;
	});

	// Sync with Redux state when it changes
	useEffect(() => {
		if (savedTheme !== undefined) {
			setIsDarkMode(savedTheme === true);
		}
	}, [savedTheme]);

	// Toggle theme function
	const toggleTheme = () => {
		setIsDarkMode(prev => !prev);
	};

	// Apply dark mode class to body
	useEffect(() => {
		// Apply dark mode class to body
		if (isDarkMode) {
			document.body.classList.add("dark-mode");
			document.body.classList.remove("light-mode");
		} else {
			document.body.classList.add("light-mode");
			document.body.classList.remove("dark-mode");
		}
	}, [isDarkMode]);

	// Select theme based on mode
	const theme = useMemo(() => (isDarkMode ? darkTheme : lightTheme), [isDarkMode]);

	const contextValue = useMemo(
		() => ({
			isDarkMode,
			toggleTheme,
			theme
		}),
		[isDarkMode, theme]
	);

	return (
		<ThemeContext.Provider value={contextValue}>
			<MuiThemeProvider theme={theme}>
				<CssBaseline />
				{children}
			</MuiThemeProvider>
		</ThemeContext.Provider>
	);
};

export default ThemeContext;
